import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { AdminUserService } from '../services/admin-user.service';
import { firstValueFrom, timeout, catchError, of, take } from 'rxjs';

export const loginGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const adminUserService = inject(AdminUserService);
  const router = inject(Router);

  // Wait for Firebase auth state to initialize (with 2 second timeout)
  let currentUser;
  try {
    currentUser = await firstValueFrom(
      authService.user$.pipe(
        take(1),
        timeout(2000),
        catchError(() => {
          // If timeout or error, fall back to synchronous check
          return of(authService.getCurrentUser());
        })
      )
    );
  } catch (error) {
    currentUser = authService.getCurrentUser();
  }
  
  if (!currentUser) {
    // No authenticated user, allow access to login page
    return true;
  }

  try {
    // User is authenticated, check admin user document
    const email = currentUser.email;
    if (!email) {
      console.error('User has no email');
      await authService.signOut();
      return true; // Allow access to login
    }

    const adminUsers = await firstValueFrom(
      adminUserService.getUserByEmail(email)
    );

    // If no admin user document or invalid, allow access to login
    if (!adminUsers || adminUsers.length === 0) {
      await authService.signOut();
      return true;
    }

    const adminUser = adminUsers[0];

    // If user is not active, allow access to login
    if (!adminUser.status || adminUser.status !== 'active') {
      await authService.signOut();
      return true;
    }

    // User is authenticated and active - redirect to admin dashboard
    // All users now use the unified /admin layout
    return router.createUrlTree(['/admin']);
  } catch (error: any) {
    console.error('Error in login guard:', error);
    // On error, allow access to login page
    return true;
  }
};

